import { Text } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { Box, FormControl, Stack } from "@mui/material";
import { NoAgreementTemplateFileAvailable } from "@src/appV2/Agreements/NoAgreementTemplateFileAvailable";
import { type Agreement, type SignedAgreement } from "@src/appV2/Agreements/types";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logError } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { PdfViewer } from "@src/appV2/PdfViewer/PdfViewer";
import { PdfViewerLoadingSkeleton } from "@src/appV2/PdfViewer/PdfViewerLoadingSkeleton";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { type Worker } from "@src/appV2/Worker/api/types";
import { type RefObject } from "react";

import { type SignedAtStage } from "../../paths";
import { useSignJobSearchAgreement } from "../api/useSignJobSearchAgreement";
import { useJobSearchAgreementForm } from "../hooks/useJobSearchAgreementForm";
import { JobSearchAgreementBanners } from "./JobSearchAgreementBanners";
import { JobSearchAgreementConfirmationCheckbox } from "./JobSearchAgreementConfirmationCheckbox";

const SUBMIT_BUTTON_TEXT = "I have read and agree to this Job Search User Agreement";

interface JobSearchAgreementFormProps {
  worker: Worker;
  agreementToSign: Agreement | SignedAgreement | undefined;
  agreementsStatus: "loading" | "error" | "success";
  onAcceptAgreement: () => Promise<void>;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  signedAtStage: SignedAtStage;
  placementId?: string;
}

export function JobSearchAgreementForm(props: JobSearchAgreementFormProps) {
  const {
    worker,
    agreementsStatus,
    onAcceptAgreement,
    agreementToSign,
    scrollContainerRef,
    signedAtStage,
    placementId,
  } = props;

  const { showErrorToast } = useToast();

  const { mutateAsync: signJobSearchAgreement, isLoading: isSigning } = useSignJobSearchAgreement({
    onSuccess: async () => {
      await onAcceptAgreement();
    },
    onError: (error) => {
      logError(APP_V2_APP_EVENTS.SIGN_JOB_SEARCH_AGREEMENT_FAILURE, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          workerName: worker.name,
          agreementVersion: agreementToSign?.attributes.version,
        },
      });
      showErrorToast("Failed to sign agreement. Please try again.");
    },
  });

  const { formMethods } = useJobSearchAgreementForm({
    workerId: worker.userId,
    agreementVersion: agreementToSign?.attributes.version,
    signedAtStage,
    placementId,
  });

  const {
    formState: { errors, isValid: formIsValid, isSubmitted: isFormSubmitted },
    control,
    register,
    handleSubmit,
  } = formMethods;

  return (
    <>
      <PageContent ref={scrollContainerRef}>
        <JobSearchAgreementBanners
          worker={worker}
          showAgreementScrolledErrorBanner={isFormSubmitted && Boolean(errors.agreementScrolled)}
          showConfirmationErrorBanner={isFormSubmitted && Boolean(errors.confirmation)}
        />

        <Box sx={{ flex: "1 1 auto", overflow: "auto", marginTop: 6 }}>
          {agreementsStatus === "loading" && <PdfViewerLoadingSkeleton />}
          {agreementsStatus === "error" && <NoAgreementTemplateFileAvailable />}
          {agreementsStatus === "success" && <PdfViewer />}
        </Box>
        <FormControl component="fieldset" variant="standard">
          <input type="hidden" {...register("agreementScrolled")} />
        </FormControl>
      </PageContent>

      <PageFooterWrapper>
        <Stack spacing={4}>
          <Text variant="body2" color="error">
            You must read the full agreement to proceed. You can then click checkbox to continue.
          </Text>

          <JobSearchAgreementConfirmationCheckbox
            worker={worker}
            control={control}
            agreementVersion={agreementToSign?.attributes.version}
            signedAtStage={signedAtStage}
            placementId={placementId}
          />

          <LoadingButton
            fullWidth
            size="large"
            variant={formIsValid ? "contained" : "outlined"}
            disabled={agreementsStatus === "loading"}
            isLoading={agreementsStatus === "loading" || isSigning}
            sx={(theme) => ({
              lineHeight: "1.5rem",
              color: formIsValid ? "white" : theme.palette.text.disabled,
            })}
            onClick={handleSubmit(async () => {
              if (!isDefined(agreementToSign)) {
                logError(APP_V2_APP_EVENTS.AGREEMENT_TO_SIGN_NOT_DEFINED, {
                  error: new Error("Job Search Agreement to sign is not defined"),
                  metadata: {
                    workerId: worker.userId,
                    workerName: worker.name,
                  },
                });
                showErrorToast(
                  "Unable to sign the agreement. Please try again or contact support."
                );
                return;
              }

              await signJobSearchAgreement({
                agreementVersion: agreementToSign.attributes.version,
                fullName: worker.name,
              });
            })}
          >
            {SUBMIT_BUTTON_TEXT}
          </LoadingButton>
        </Stack>
      </PageFooterWrapper>
    </>
  );
}
